import { get } from "@ember/object"
import type {
  Card,
  CardAttachment,
  CardStack,
  GoogleClassroomClassSetting,
  ShareModeSettings,
} from "@shared/models/card-stack"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type EmberStore from "cc-frontend/services/ember-store"
import { findGoogleClassroomCard } from "cc-frontend/utils/card-stack/filter-cards"
import type {
  GoogleClassroomCardEditor,
  GoogleClassroomTextProp,
  NewAttachment,
} from "./card-editor"

/**
 * GoogleClassroomCardEditor for legacy card stacks: every edit goes through the
 * lesson-editor service, patching the embedded card in `cardStack.attributes.cards`
 * like the sidebar always has.
 */
export class LegacyGoogleClassroomCardEditor implements GoogleClassroomCardEditor {
  constructor(
    private readonly deps: {
      store: EmberStore
      lessonEditor: LessonEditor
      modelsNeededForFirstEdit: ModelsNeededForFirstEdit
      cardStackId: string
      cardId: string
      dateString: string
    }
  ) {
    // Warm the doc, like the modal's old cardStack FindDoc (usually already in memory).
    void deps.store.find("card-stack", deps.cardStackId)
  }

  /**
   * Re-found from the store's cards array on every read: the card object in memory
   * after e.g. a template edit is not the same object, so holding one would go stale
   * (see the note that used to live on the modal's card getter).
   */
  get card() {
    const cardStack = this.deps.store.findInMemory("card-stack", this.deps.cardStackId) as
      | CardStack
      | undefined
    const cards = cardStack && (get(cardStack, "attributes.cards") as Array<Card> | null)
    if (!cards) return undefined
    return findGoogleClassroomCard(cards, this.deps.cardId)
  }

  editProp(prop: GoogleClassroomTextProp, value: string | null) {
    this.deps.lessonEditor.editCard(
      this.deps.modelsNeededForFirstEdit,
      this.deps.cardStackId,
      this.deps.cardId,
      prop,
      value
    )
  }

  finishEditingProp(
    prop: GoogleClassroomTextProp,
    value: string | null,
    originalValue: string | null,
    wordCountChange: number
  ) {
    this.deps.lessonEditor.finishEditingGoogleClassroomCard(
      this.deps.modelsNeededForFirstEdit,
      this.deps.cardStackId,
      this.deps.cardId,
      prop,
      value,
      originalValue,
      wordCountChange
    )
  }

  setTopic(topic: string | null) {
    this.deps.lessonEditor.setGoogleClassroomTopic(
      this.deps.modelsNeededForFirstEdit,
      topic,
      this.deps.cardStackId,
      this.deps.cardId
    )
  }

  setChoices(choices: Array<string>) {
    this.deps.lessonEditor.setMultipleChoiceQuestions(
      this.deps.modelsNeededForFirstEdit,
      this.deps.cardStackId,
      this.deps.cardId,
      choices
    )
  }

  addAttachments(files: Array<NewAttachment>, source: string) {
    this.deps.lessonEditor.addAttachments(
      this.deps.modelsNeededForFirstEdit,
      files,
      this.deps.cardStackId,
      this.deps.cardId,
      this.card?.attributes.attachments ?? [],
      source
    )
  }

  upsertShareMode(attachmentId: string, shareMode: ShareModeSettings) {
    this.deps.lessonEditor.upsertShareMode(
      attachmentId,
      shareMode,
      this.deps.cardStackId,
      this.deps.cardId
    )
  }

  removeAttachment(attachment: CardAttachment) {
    this.deps.lessonEditor.removeAttachment(
      this.deps.modelsNeededForFirstEdit,
      this.deps.cardStackId,
      this.deps.cardId,
      attachment
    )
  }

  scheduleClass(classSetting: GoogleClassroomClassSetting, className: string) {
    const card = this.card
    if (!card) return
    this.deps.lessonEditor.scheduleGoogleClass(
      this.deps.modelsNeededForFirstEdit,
      this.deps.cardStackId,
      card,
      classSetting,
      className,
      this.deps.dateString
    )
  }

  unscheduleClass(classSetting: GoogleClassroomClassSetting, className: string) {
    const card = this.card
    if (!card) return
    this.deps.lessonEditor.unscheduleGoogleClass(
      this.deps.cardStackId,
      card,
      classSetting,
      className,
      this.deps.dateString
    )
  }

  updateClassSetting(
    classSetting: GoogleClassroomClassSetting,
    prop: "scheduledAtTime" | "dueAt",
    value: GoogleClassroomClassSetting["scheduledAtTime"] | GoogleClassroomClassSetting["dueAt"]
  ) {
    const card = this.card
    if (!card) return
    this.deps.lessonEditor.updateGoogleClass(this.deps.cardStackId, card, classSetting, value, prop)
  }
}
